import gsap, { Power2 } from 'gsap';
import { GetBy } from '../_app/cuchillo/core/Element';

const PRELOAD_AHEAD = 2;

export default class VisorVideos {
  _blur = 0;
  get blur() { return this._blur; }
  set blur(__n) {
    this._blur = __n;
    this.container?.style.setProperty('--blur', `${this._blur}px`);
  }

  _overlay = 0;
  get overlay() { return this._overlay; }
  set overlay(__n) {
    this._overlay = __n;
    this.container?.style.setProperty('--overlay', `${this._overlay}`);
  }

  _scale = 0;
  get scale() { return this._scale; }
  set scale(__n) {
    this._scale = __n;
    this.container?.style.setProperty('--scale', `${this._scale}`);
  }

  _grayscale = 0;
  get grayscale() { return this._grayscale; }
  set grayscale(__n) {
    this._grayscale = __n;
    this.container?.style.setProperty('--grayscale', `${this._grayscale}`);
  }

  constructor(__dom, options = {}) {
    this.hasFocus = false;
    this.current = -1;
    this.container = __dom;

    this.isLoop = options.loop ?? true;
    this.isPaused = options.autoplay === false;

    this.controls = GetBy.selector('[data-controls]', __dom)[0] || null;
    this.buttons = this.controls ? [...GetBy.selector('button[data-video]', this.controls)] : [];

    this._raf = null;
    this._boundButtonHandlers = [];

    this.videos = [...GetBy.selector('figure', __dom)].map(
      (figure, i) => new VisorVideo__Video(figure, i, this)
    );

    this._bindControls();
    this._paintButtons();
  }

  get currentVideo() {
    if (this.current < 0) return null;
    return this.videos[this.current] || null;
  }

  _bindControls() {
    if (!this.buttons.length) return;

    this.buttons.forEach((btn) => {
      const handler = () => {
        const index = parseInt(btn.dataset.video, 10);
        if (!Number.isNaN(index)) this.change(index);
      };

      btn.addEventListener('click', handler);
      this._boundButtonHandlers.push({ btn, handler });
    });
  }

  change(__n) {
    if (__n < 0 || __n >= this.videos.length) return;
    if (this.current === __n) return;

    this._stopProgressLoop();

    if (this.current >= 0) this.videos[this.current].hide();

    this.current = __n;

    this.videos.forEach((v, i) => {
      const isAhead = i > __n && i <= __n + PRELOAD_AHEAD;
      if (!isAhead && i !== __n && !v.isLoaded) {
        v.cancel();
      }
    });

    this._paintButtons();

    this.videos[__n].show({
      autoplay: !this.isPaused,
      onReady: () => {
        this._paintButtons();
        this._preloadAhead(__n);

        if (!this.isPaused) {
          this._startProgressLoop();
        }
      }
    });
  }

  next() {
    if (!this.videos.length) return;
    const nextIndex = (this.current + 1) % this.videos.length;
    this.change(nextIndex);
  }

  prev() {
    if (!this.videos.length) return;
    const prevIndex = (this.current - 1 + this.videos.length) % this.videos.length;
    this.change(prevIndex);
  }

  async play() {
    this.isPaused = false;

    const item = this.currentVideo;
    if (!item) return;

    await item.play();
    this._startProgressLoop();
    this._paintButtons();
  }

  pause() {
    this.isPaused = true;

    const item = this.currentVideo;
    if (!item) return;

    item.pause();
    this._stopProgressLoop();
    this._paintButtons();
  }

  toggle() {
    const item = this.currentVideo;
    if (!item) return;

    if (item.video.paused) {
      return this.play();
    }

    this.pause();
  }

  _preloadAhead(__from) {
    const loadNext = (index) => {
      if (index >= this.videos.length || index > __from + PRELOAD_AHEAD) return;
      if (this.current !== __from) return;

      const video = this.videos[index];
      if (video.isLoaded) {
        loadNext(index + 1);
      } else {
        video.load(() => loadNext(index + 1));
      }
    };

    loadNext(__from + 1);
  }

  _getCurrentProgress() {
    if (this.current < 0) return 0;

    const currentVideo = this.currentVideo?.video;
    if (!currentVideo) return 0;

    const duration = currentVideo.duration;
    const currentTime = currentVideo.currentTime;

    if (!Number.isFinite(duration) || duration <= 0) return 0;

    return Math.max(0, Math.min(1, currentTime / duration));
  }

  _paintButtons() {
    if (!this.buttons.length) return;

    this.buttons.forEach((btn, i) => {
      let progress = 0;

      if (this.current > i) {
        progress = 1;
      } else if (this.current < i) {
        progress = 0;
      } else {
        progress = this._getCurrentProgress();
      }

      btn.classList.toggle('is-active', i === this.current);
      btn.style.setProperty('--progress', `${progress}`);
    });
  }

  _startProgressLoop() {
    this._stopProgressLoop();

    const update = () => {
      if (this.isPaused) {
        this._raf = null;
        return;
      }

      this._paintButtons();

      if (this.current >= 0 && this.videos[this.current]?.isShow) {
        this._raf = requestAnimationFrame(update);
      } else {
        this._raf = null;
      }
    };

    this._raf = requestAnimationFrame(update);
  }

  _stopProgressLoop() {
    if (this._raf) {
      cancelAnimationFrame(this._raf);
      this._raf = null;
    }
  }

  show() {
    if (this.hasFocus) {
      this.container.style.opacity = 1;
    } else {
      gsap.to(this.container, { alpha: 1, duration: 0.4, ease: Power2.easeIn });
    }
  }

  hide() {
    this._stopProgressLoop();

    if (this.hasFocus) {
      this.container.style.opacity = 0;
    } else {
      gsap.to(this.container, { alpha: 0, duration: 0.4, ease: Power2.easeIn });
    }
  }

  dispose() {
    this._stopProgressLoop();

    this._boundButtonHandlers.forEach(({ btn, handler }) => {
      btn.removeEventListener('click', handler);
    });

    this._boundButtonHandlers = [];

    this.videos.forEach(v => v.dispose());
    this.videos = [];
    this.buttons = [];
    this.controls = null;
    this.container = null;
  }
}

class VisorVideo__Video {
  constructor(dom, index, visor) {
    this.visor = visor;
    this.index = index;
    this.isLoaded = false;
    this.isLoading = false;
    this.isShow = false;
    this.container = dom;

    this.img = GetBy.selector('img', dom)[0];
    this.video = GetBy.selector('video', dom)[0];

    this._src = this.video.getAttribute('src');
    this.video.removeAttribute('src');

    this._onLoaded = null;

    this._boundHandleLoaded = this._handleLoaded.bind(this);
    this._boundHandleEnded = this._handleEnded.bind(this);

    this.video.loop = this.visor.isLoop;
    this.video.addEventListener('ended', this._boundHandleEnded);
  }

  load(onComplete = null) {
    if (this.isLoaded) {
      onComplete?.();
      return;
    }

    if (this.isLoading) {
      const prev = this._onLoaded;
      this._onLoaded = () => {
        prev?.();
        onComplete?.();
      };
      return;
    }

    this.isLoading = true;
    this._onLoaded = onComplete;

    this.video.addEventListener('canplaythrough', this._boundHandleLoaded, { once: true });
    this.video.src = this._src;
    this.video.preload = 'auto';
    this.video.load();
  }

  _handleLoaded() {
    this.isLoaded = true;
    this.isLoading = false;
    this.width = this.video.videoWidth;
    this.height = this.video.videoHeight;

    this._onLoaded?.();
    this._onLoaded = null;
  }

  _handleEnded() {
    if (this.visor.isLoop) return;
    if (!this.isShow) return;
    if (this.visor.isPaused) return;

    this.visor.next();
  }

  cancel() {
    if (!this.isLoading || this.isLoaded) return;

    this.video.removeEventListener('canplaythrough', this._boundHandleLoaded);
    this.video.src = '';
    this.video.load();
    this.isLoading = false;
    this._onLoaded = null;
  }

  async play() {
    try {
      await this.video.play();
    } catch (e) { console.warn(e); }
  }

  pause() {
    this.video.pause();
  }

  show({ autoplay = true, onReady = null } = {}) {
    if (this.isShow) return;
    this.isShow = true;

    this.video.loop = this.visor.isLoop;

    const reveal = async () => {
      this.video.currentTime = 0;

      if (autoplay) {
        await this.play();
      } else {
        this.pause();
      }

      onReady?.();
    };

    if (this.isLoaded) {
      reveal();
    } else {
      this.load(reveal);
    }

    gsap.killTweensOf(this.container);
    gsap.to(this.container, {
      alpha: 1,
      duration: 0.2,
      ease: Power2.easeIn
    });
  }

  hide() {
    if (!this.isShow) return;
    this.isShow = false;

    gsap.killTweensOf(this.container);
    gsap.to(this.container, {
      alpha: 0,
      duration: 0.3,
      ease: Power2.easeIn,
      onComplete: () => {
        this.pause();
      }
    });
  }

  dispose() {
    this.cancel();
    this.video.removeEventListener('ended', this._boundHandleEnded);
    this.video.removeEventListener('canplaythrough', this._boundHandleLoaded);
    this.pause();
    this.video.src = '';
    this.video.load();

    this.container = null;
    this.video = null;
    this.img = null;
  }
}